import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {AllowanceEntity, BokkzTslDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, Observable, shareReplay, Subscription, switchMap, take, tap} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AsyncPipe} from "@angular/common";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-voorschot',
  templateUrl: './voorschot.component.html',
  styleUrls: ['./voorschot.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    AsyncPipe
  ]
})
export class VoorschotComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzTslDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();

  public formGroup = new FormGroup({
    alOntvangenZorgtoeslag: new FormControl<number | null>(null),
    alOntvangenHuurtoeslag: new FormControl<number | null>(null),
    alOntvangenKOT: new FormControl<number | null>(null),
    alOntvangenKGB: new FormControl<number | null>(null),
  });

  readonly allowanceEntity$: Observable<AllowanceEntity> =
    this.dataService.allowanceEntity$.pipe(
      filter((data): data is AllowanceEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.allowanceEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((data) => {
      this.formGroup.patchValue({
          alOntvangenZorgtoeslag: data.toeslagen?.zorgtoeslag?.alOntvangenZorgtoeslag,
          alOntvangenHuurtoeslag: data.toeslagen?.huurtoeslag?.alOntvangenHuurtoeslag,
          alOntvangenKGB: data.toeslagen?.kgb?.alOntvangenKGB,
          alOntvangenKOT: data.toeslagen?.kot?.alOntvangenKOT
        },
        {emitEvent: false});
    })
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      this.formGroup.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          switchMap(() => this.dataService.calculateLatestAllowances()),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(),
    );
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const rawValue = this.formGroup.getRawValue();

    this.dataService.patchAllowanceEntity((currentEntity) => {
      const toeslagen: any = {
        ...currentEntity.toeslagen,
        zorgtoeslag: {
          ...currentEntity.toeslagen?.zorgtoeslag,
          alOntvangenZorgtoeslag: rawValue.alOntvangenZorgtoeslag,
        },
        huurtoeslag: {
          ...currentEntity.toeslagen?.huurtoeslag,
          alOntvangenHuurtoeslag: rawValue.alOntvangenHuurtoeslag,
        },
        kgb: {
          ...currentEntity.toeslagen?.kgb,
          alOntvangenKGB: rawValue.alOntvangenKGB,
        },
        kot: {
          ...currentEntity.toeslagen?.kot,
          alOntvangenKOT: rawValue.alOntvangenKOT
        }
      } as AllowanceEntity;
      return {
        ...currentEntity,
        toeslagen,
      };
    });
  }
}
